import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { recipes, extract } from '../lib/api';

function RecipeNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isFromExtension = searchParams.get('from') === 'extension';

  const [mode, setMode] = useState('manual'); // manual, transcript, paste
  const [transcript, setTranscript] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractError, setExtractError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    sourceUrl: '',
    sourcePlatform: 'manual',
    sourceCreator: '',
    prepTime: '',
    cookTime: '',
    totalTime: '',
    servings: '',
    ingredients: [{ item: '', amount: '', unit: '', notes: '' }],
    instructions: [{ step: 1, text: '' }],
    notes: [],
  });

  // Load pending recipe from extension if available
  useEffect(() => {
    if (isFromExtension && typeof chrome !== 'undefined' && chrome.storage) {
      chrome.storage.local.get(['pendingRecipe'], (result) => {
        if (result.pendingRecipe) {
          setFormData({
            ...formData,
            ...result.pendingRecipe,
            ingredients: result.pendingRecipe.ingredients || formData.ingredients,
            instructions: result.pendingRecipe.instructions || formData.instructions,
          });
          chrome.storage.local.remove(['pendingRecipe']);
        }
      });
    }
  }, [isFromExtension]);

  const createMutation = useMutation({
    mutationFn: (data) => recipes.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      navigate(`/recipe/${data.id}`);
    },
  });

  const handleExtract = async () => {
    if (!transcript.trim()) {
      setExtractError('Please enter a transcript');
      return;
    }

    setIsExtracting(true);
    setExtractError('');

    try {
      const result = await extract.fromTranscript(transcript);
      setFormData({
        ...formData,
        title: result.title || '',
        description: result.description || '',
        prepTime: result.prepTime || '',
        cookTime: result.cookTime || '',
        totalTime: result.totalTime || '',
        servings: result.servings || '',
        ingredients: result.ingredients || formData.ingredients,
        instructions: result.instructions || formData.instructions,
        notes: result.notes || [],
      });
      setMode('manual');
    } catch (err) {
      setExtractError(err.message || 'Failed to extract recipe');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate
    if (!formData.title.trim()) {
      return;
    }

    // Filter out empty ingredients and instructions
    const cleanedData = {
      ...formData,
      ingredients: formData.ingredients.filter((i) => i.item.trim()),
      instructions: formData.instructions
        .filter((i) => i.text.trim())
        .map((i, idx) => ({ ...i, step: idx + 1 })),
    };

    createMutation.mutate(cleanedData);
  };

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { item: '', amount: '', unit: '', notes: '' }],
    });
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length <= 1) return;
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index),
    });
  };

  const addInstruction = () => {
    setFormData({
      ...formData,
      instructions: [...formData.instructions, { step: formData.instructions.length + 1, text: '' }],
    });
  };

  const updateInstruction = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = { step: index + 1, text: value };
    setFormData({ ...formData, instructions: newInstructions });
  };

  const removeInstruction = (index) => {
    if (formData.instructions.length <= 1) return;
    const newInstructions = formData.instructions
      .filter((_, i) => i !== index)
      .map((inst, i) => ({ ...inst, step: i + 1 }));
    setFormData({ ...formData, instructions: newInstructions });
  };

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Add New Recipe</h1>
        <button onClick={() => navigate(-1)} className="btn btn-ghost">
          Cancel
        </button>
      </div>

      {/* Mode selector */}
      <div className="card p-4 mb-8">
        <div className="flex gap-2">
          <button
            onClick={() => setMode('manual')}
            className={`btn flex-1 ${mode === 'manual' ? 'btn-primary' : 'btn-secondary'}`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Manual Entry
          </button>
          <button
            onClick={() => setMode('transcript')}
            className={`btn flex-1 ${mode === 'transcript' ? 'btn-primary' : 'btn-secondary'}`}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            From Transcript
          </button>
        </div>
      </div>

      {/* Transcript input mode */}
      {mode === 'transcript' && (
        <div className="card p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Paste Video Transcript
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Paste the transcript from a cooking video and we'll use AI to extract the recipe.
          </p>
          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            className="input min-h-[200px] mb-4"
            placeholder="Paste the video transcript here..."
            rows={8}
          />
          {extractError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg mb-4">
              {extractError}
            </div>
          )}
          <button
            onClick={handleExtract}
            disabled={isExtracting}
            className="btn btn-primary w-full"
          >
            {isExtracting ? (
              <>
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Extracting Recipe...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                Extract Recipe
              </>
            )}
          </button>
        </div>
      )}

      {/* Manual form */}
      {mode === 'manual' && (
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="card p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input"
                placeholder="Recipe title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input min-h-[100px]"
                placeholder="Brief description of the recipe"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Source URL
                </label>
                <input
                  type="url"
                  value={formData.sourceUrl}
                  onChange={(e) => setFormData({ ...formData, sourceUrl: e.target.value })}
                  className="input"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Creator
                </label>
                <input
                  type="text"
                  value={formData.sourceCreator}
                  onChange={(e) => setFormData({ ...formData, sourceCreator: e.target.value })}
                  className="input"
                  placeholder="@username"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prep Time
                </label>
                <input
                  type="text"
                  value={formData.prepTime}
                  onChange={(e) => setFormData({ ...formData, prepTime: e.target.value })}
                  className="input"
                  placeholder="15 mins"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cook Time
                </label>
                <input
                  type="text"
                  value={formData.cookTime}
                  onChange={(e) => setFormData({ ...formData, cookTime: e.target.value })}
                  className="input"
                  placeholder="30 mins"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Total Time
                </label>
                <input
                  type="text"
                  value={formData.totalTime}
                  onChange={(e) => setFormData({ ...formData, totalTime: e.target.value })}
                  className="input"
                  placeholder="45 mins"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Servings
                </label>
                <input
                  type="text"
                  value={formData.servings}
                  onChange={(e) => setFormData({ ...formData, servings: e.target.value })}
                  className="input"
                  placeholder="4 servings"
                />
              </div>
            </div>
          </div>

          {/* Ingredients */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ingredients *</h2>
              <button type="button" onClick={addIngredient} className="btn btn-secondary text-sm">
                + Add
              </button>
            </div>

            <div className="space-y-3">
              {formData.ingredients.map((ing, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <input
                    type="text"
                    value={ing.amount}
                    onChange={(e) => updateIngredient(index, 'amount', e.target.value)}
                    className="input w-20"
                    placeholder="Qty"
                  />
                  <input
                    type="text"
                    value={ing.unit}
                    onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                    className="input w-24"
                    placeholder="Unit"
                  />
                  <input
                    type="text"
                    value={ing.item}
                    onChange={(e) => updateIngredient(index, 'item', e.target.value)}
                    className="input flex-1"
                    placeholder="Ingredient"
                  />
                  {formData.ingredients.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      className="btn-icon text-red-500"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Instructions */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Instructions *</h2>
              <button type="button" onClick={addInstruction} className="btn btn-secondary text-sm">
                + Add Step
              </button>
            </div>

            <div className="space-y-3">
              {formData.instructions.map((inst, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </span>
                  <textarea
                    value={inst.text}
                    onChange={(e) => updateInstruction(index, e.target.value)}
                    className="input flex-1 min-h-[80px]"
                    placeholder="Describe this step..."
                    rows={2}
                  />
                  {formData.instructions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeInstruction(index)}
                      className="btn-icon text-red-500"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 justify-end">
            <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="btn btn-primary"
            >
              {createMutation.isPending ? 'Saving...' : 'Save Recipe'}
            </button>
          </div>

          {createMutation.error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
              {createMutation.error.message}
            </div>
          )}
        </form>
      )}
    </div>
  );
}

export default RecipeNew;
